import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Container, Row, Col, Button, Table, Spinner, Form } from "react-bootstrap";
import { ArrowLeft as Icon_Back } from "react-bootstrap-icons";

import DetalleCalculosEvaluado from "./DetalleCalculosEvaluado";
import { esAdmin, esSuperadmin } from "../config/superadmins";
import { getReporte, getAnalisis } from "../actions";

const ID_PROCESO = "260521";
const TOKEN = "c3649ba4e3a81815";

// Mapa variable → columnas AVG_/STDEV_ de Listado1Total (totales). El nombre
// que devuelve /analisis (dbo.variable.nombre) no siempre coincide con el
// sufijo de la columna estadística (ej. "Exceso de Confianza" → Confianza,
// "Autorregulación" → Autoregulacion), por eso cada entrada lista sus alias.
const VAR_STATS = [
  // Riesgos
  { aliases: ["Exceso de Confianza", "Confianza"], avgKey: "AVG_Confianza", sdKey: "STDEV_Confianza" },
  { aliases: ["Pesimismo"], avgKey: "AVG_Pesimismo", sdKey: "STDEV_Pesimismo" },
  { aliases: ["Desesperanza"], avgKey: "AVG_Desesperanza", sdKey: "STDEV_Desesperanza" },
  { aliases: ["Ineficacia"], avgKey: "AVG_Ineficacia", sdKey: "STDEV_Ineficacia" },
  { aliases: ["Fragilidad"], avgKey: "AVG_Fragilidad", sdKey: "STDEV_Fragilidad" },
  { aliases: ["Cinismo"], avgKey: "AVG_Cinismo", sdKey: "STDEV_Cinismo" },
  { aliases: ["Superstición", "Supersticion"], avgKey: "AVG_Supersticion", sdKey: "STDEV_Supersticion" },
  { aliases: ["Influencia Social", "Influencia"], avgKey: "AVG_Influencia", sdKey: "STDEV_Influencia" },
  { aliases: ["Sobre valor de sí mismo", "Sesgo de Sobreconfianza", "Sobreconfianza", "Sesgo SobreValoracion", "Sesgo_SobreValoracion"], avgKey: "AVG_Sesgo_SobreValoracion", sdKey: "STDEV_Sesgo_SobreValoracion" },
  // Antifortalezas
  { aliases: ["Prudencia"], avgKey: "AVG_Prudencia", sdKey: "STDEV_Prudencia" },
  { aliases: ["Autorregulación", "Autoregulación", "Autoregulacion"], avgKey: "AVG_Autoregulacion", sdKey: "STDEV_Autoregulacion" },
  { aliases: ["Responsabilidad"], avgKey: "AVG_Responsabilidad", sdKey: "STDEV_Responsabilidad" },
  { aliases: ["Vitalidad"], avgKey: "AVG_Vitalidad", sdKey: "STDEV_Vitalidad" },
  { aliases: ["Apertura"], avgKey: "AVG_Apertura", sdKey: "STDEV_Apertura" },
  // Fortalezas
  { aliases: ["Optimismo"], avgKey: "AVG_Optimismo", sdKey: "STDEV_Optimismo" },
  { aliases: ["Esperanza"], avgKey: "AVG_Esperanza", sdKey: "STDEV_Esperanza" },
  { aliases: ["Autoeficacia"], avgKey: "AVG_Autoeficacia", sdKey: "STDEV_Autoeficacia" },
  { aliases: ["Resiliencia"], avgKey: "AVG_Resiliencia", sdKey: "STDEV_Resiliencia" },
  // Juicio
  { aliases: ["Juicio"], avgKey: "AVG_Juicio", sdKey: "STDEV_Juicio" },
];

// Normaliza nombres para emparejar (sin acentos, minúsculas, solo alfanumérico).
const _normVar = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .toLowerCase()
    .replace(new RegExp("[^a-z0-9]", "g"), "");

// Índice alias-normalizado → {avgKey, sdKey} (construido una sola vez).
const STATS_POR_VARIABLE = {};
VAR_STATS.forEach((v) =>
  v.aliases.forEach((a) => {
    STATS_POR_VARIABLE[_normVar(a)] = v;
  }),
);

// La pregunta 20305 (Sesgo de Sobreconfianza, tipo_calculo = 5) guarda su
// respuesta como JSON {"range01":"4",...,"range10":"7"}: 10 subpreguntas con
// valor 1..10. Si texto_respuesta es ese JSON, devuelve [{n, val}]; si no, null.
const _parseRanges = (texto) => {
  if (!texto || typeof texto !== "string") return null;
  const t = texto.trim();
  if (!t.startsWith("{")) return null;
  try {
    const obj = JSON.parse(t);
    const keys = Object.keys(obj).filter((k) => /^range\d+$/i.test(k));
    if (keys.length === 0) return null;
    return keys
      .sort(
        (a, b) =>
          parseInt(a.replace(/\D/g, ""), 10) -
          parseInt(b.replace(/\D/g, ""), 10),
      )
      .map((k) => ({ n: parseInt(k.replace(/\D/g, ""), 10), val: obj[k] }));
  } catch {
    return null;
  }
};

// Ficha detallada de un evaluado. Accesible para admin (perfil 2 y 3):
//  - Perfil 2 (cliente): solo el resumen de resultados (DetalleCalculosEvaluado),
//    equivalente al que se ve al expandir la fila (chevron) en el reporte.
//  - Perfil 3 (superadmin): además el detalle pregunta a pregunta obtenido del
//    endpoint /analisis (dbo.GetAnalisisEvaluado).
const FichaEvaluado = () => {
  const { idUsuario } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ticket = useSelector((state) => state.ticket);
  const {
    rows: _listado1,
    totales: _totales,
    norma: _norma,
  } = useSelector((state) => state.reporte);

  // Perfil 3 (superadmin) ve el detalle por pregunta; perfil 2 solo el resumen.
  const _esSuper = esSuperadmin(ticket);

  const [analisis, setAnalisis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroVariable, setFiltroVariable] = useState("");

  // Carga el reporte (si no está en redux) y el análisis por pregunta.
  useEffect(() => {
    if (!_listado1 || _listado1.length === 0) {
      dispatch(
        getReporte({
          token: TOKEN,
          function: "GetReporte",
          params: { id_proceso: ID_PROCESO },
        }),
      );
    }

    // El detalle por pregunta (/analisis) es solo para superadmin (perfil 3).
    if (_esSuper) {
      dispatch(
        getAnalisis({
          token: TOKEN,
          function: "GetAnalisisEvaluado",
          params: { id_usuario: Number(idUsuario), id_proceso: ID_PROCESO },
        }),
      )
        .then((resp) => setAnalisis((resp && resp.data) || []))
        .catch(() => setError("No se pudo cargar el detalle de respuestas."))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // Mount-only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUsuario]);

  // Defensa: admin (perfil 2 o 3). Perfil 2 ve solo el resumen.
  if (!esAdmin(ticket)) {
    return <Navigate to="/" replace={true} />;
  }

  const item = (_listado1 || []).find(
    (r) => String(r.id_usuario) === String(idUsuario),
  );

  const Volver = (
    <Button
      variant="outline-secondary"
      size="sm"
      onClick={() => navigate("/reporte/260521")}
    >
      <Icon_Back size={16} className="me-1" />
      Volver a la lista
    </Button>
  );

  // z-score de la respuesta del evaluado respecto a la cohorte (por pregunta).
  const zScore = (p) => {
    const punt = p.puntaje;
    const avg = p.promedio_puntaje;
    const sd = p.desviacion_puntaje;
    if (punt == null || avg == null || sd == null || Number(sd) === 0)
      return null;
    return (Number(punt) - Number(avg)) / Number(sd);
  };

  // Orden por GRUPO (instrumento), no alfabético. Como /analisis no devuelve
  // id_variable, derivamos el orden de cada variable por (min id_instrumento,
  // min id_pregunta) de sus filas; las preguntas sin variable van al final.
  const ordenGrupo = {};
  analisis.forEach((p) => {
    const v = p.variable || "￿"; // sin variable → al final
    const inst = Number(p.id_instrumento) || 9999;
    const preg = Number(p.id_pregunta) || 0;
    if (
      !ordenGrupo[v] ||
      inst < ordenGrupo[v].inst ||
      (inst === ordenGrupo[v].inst && preg < ordenGrupo[v].preg)
    ) {
      ordenGrupo[v] = { inst, preg };
    }
  });
  const claveGrupo = (v) => {
    const g = ordenGrupo[v || "￿"] || { inst: 9999, preg: 0 };
    return g.inst * 100000 + g.preg;
  };

  // Lista de variables presentes, ordenada por grupo (para el select de filtro).
  const variablesPresentes = Array.from(
    new Set(analisis.map((p) => p.variable).filter(Boolean)),
  ).sort((a, b) => claveGrupo(a) - claveGrupo(b));

  // Detalle ordenado por grupo y luego por pregunta, aplicando el filtro.
  const detalleOrdenado = [...analisis]
    .filter((p) => !filtroVariable || p.variable === filtroVariable)
    .sort((a, b) => {
      const ka = claveGrupo(a.variable);
      const kb = claveGrupo(b.variable);
      if (ka !== kb) return ka - kb;
      return Number(a.id_pregunta) - Number(b.id_pregunta);
    });

  // Puntaje del evaluado por variable = suma de los puntajes de sus preguntas
  // (coincide con la columna homónima de Resultado_<proceso>, ver GetAnalisisEvaluado).
  const sumaPorVariable = {};
  detalleOrdenado.forEach((p) => {
    const k = p.variable || "Sin variable de cálculo";
    sumaPorVariable[k] = (sumaPorVariable[k] || 0) + (Number(p.puntaje) || 0);
  });

  // Estadísticas de una variable (promedio, desv. estándar, Z-score) para la
  // fila de encabezado. Devuelve tanto el grupo (lote) como la NORMA de
  // referencia (misma base que el flag de riesgo). Solo superadmin las ve.
  const statsVariable = (varNombre) => {
    const cfg = STATS_POR_VARIABLE[_normVar(varNombre)];
    if (!cfg || !_totales) return null;
    const avg = parseFloat(_totales[cfg.avgKey]);
    const sd = parseFloat(_totales[cfg.sdKey]);
    if (isNaN(avg) || isNaN(sd)) return null;
    const puntaje = sumaPorVariable[varNombre] || 0;
    const z = sd !== 0 ? (puntaje - avg) / sd : null;

    // Norma de referencia (puede no estar disponible → solo se muestra grupo).
    let normaAvg = null;
    let normaSd = null;
    let normaZ = null;
    if (_norma) {
      const na = parseFloat(_norma[cfg.avgKey]);
      const ns = parseFloat(_norma[cfg.sdKey]);
      if (!isNaN(na) && !isNaN(ns)) {
        normaAvg = na;
        normaSd = ns;
        normaZ = ns !== 0 ? (puntaje - na) / ns : null;
      }
    }
    return { puntaje, avg, sd, z, normaAvg, normaSd, normaZ };
  };

  return (
    <Container fluid="xl" className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="xz-titulo1 mb-0" style={{ fontSize: "1.20rem" }}>
          Ficha detallada del evaluado
        </h4>
        {Volver}
      </div>

      {item ? (
        <>
          <Row className="mb-2">
            <Col md={6}>
              <span className="fw-bold">{item.nombre}</span>{" "}
              <span className="text-secondary">— {item.rut}</span>
            </Col>
            <Col md={6} className="text-md-end">
              <span
                className="fw-bold"
                style={{
                  color: item.RESULTADO === "APTO" ? "#2e7d32" : "#c0504d",
                }}
              >
                {item.RESULTADO}
              </span>{" "}
              <span className="text-secondary">· Proceso {ID_PROCESO}</span>
            </Col>
          </Row>

          {/* Mismos cálculos que la fila opcional del reporte */}
          <DetalleCalculosEvaluado
            item={item}
            totales={_totales}
            norma={_norma}
          />
        </>
      ) : (
        <p className="text-secondary">
          No se encontró el evaluado en el reporte cargado.
        </p>
      )}

      {_esSuper && (
        <>
      <hr />
      <h5 className="xz-titulo1" style={{ fontSize: "1.05rem" }}>
        Detalle por pregunta
      </h5>
      <p className="text-secondary" style={{ fontSize: "0.8rem" }}>
        Comparación de cada respuesta contra el promedio y desviación estándar de
        la cohorte del proceso {ID_PROCESO}.
      </p>

      {!loading && !error && variablesPresentes.length > 0 && (
        <Row className="mb-2">
          <Col xs={12} md={4}>
            <Form.Select
              size="sm"
              value={filtroVariable}
              onChange={(e) => setFiltroVariable(e.target.value)}
            >
              <option value="">Todas las variables</option>
              {variablesPresentes.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" size="sm" />
          <span className="text-secondary ms-2">Cargando respuestas...</span>
        </div>
      ) : error ? (
        <p className="text-danger">{error}</p>
      ) : detalleOrdenado.length === 0 ? (
        <p className="text-secondary">Sin respuestas registradas.</p>
      ) : (
        <div className="xz-table-scroll-container">
          <Table
            className="xz-table-esg"
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <thead>
              <tr>
                <th style={{ width: "5%" }} className="text-center">
                  Instr.
                </th>
                <th style={{ width: "6%" }} className="text-center">
                  Pregunta
                </th>
                <th style={{ width: "34%" }}>Texto de la pregunta</th>
                <th style={{ width: "5%" }} className="text-center">
                  Resp.
                </th>
                <th style={{ width: "20%" }}>Opción escogida</th>
                <th className="text-center" style={{ width: "7%" }}>
                  Puntaje
                </th>
                <th className="text-center" style={{ width: "8%" }}>
                  Promedio
                </th>
                <th className="text-center" style={{ width: "8%" }}>
                  Desv. Est.
                </th>
                <th className="text-center" style={{ width: "7%" }}>
                  vs Grupo
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filas = [];
                let variableActual = null;
                detalleOrdenado.forEach((p, idx) => {
                  const varNombre = p.variable || "Sin variable de cálculo";
                  if (varNombre !== variableActual) {
                    variableActual = varNombre;
                    const st = statsVariable(varNombre);
                    filas.push(
                      <tr key={`grp_${idx}`}>
                        <td
                          colSpan={9}
                          style={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "1rem",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-baseline">
                            <span>{varNombre}</span>
                            {st && (
                              <span
                                className="text-end"
                                style={{ fontWeight: 500, fontSize: "0.8rem" }}
                              >
                                Puntaje {st.puntaje}
                                {st.normaAvg != null && (
                                  <>
                                    <span className="mx-2">|</span>
                                    Norma · Prom {st.normaAvg.toFixed(2)} · DE{" "}
                                    {st.normaSd.toFixed(2)}
                                    {st.normaZ != null && (
                                      <>
                                        {" "}
                                        · Z {st.normaZ > 0 ? "+" : ""}
                                        {st.normaZ.toFixed(2)}σ
                                      </>
                                    )}
                                  </>
                                )}
                                <span className="mx-2">|</span>
                                Grupo · Prom {st.avg.toFixed(2)} · DE{" "}
                                {st.sd.toFixed(2)}
                                {st.z != null && (
                                  <>
                                    {" "}
                                    · Z {st.z > 0 ? "+" : ""}
                                    {st.z.toFixed(2)}σ
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>,
                    );
                  }
                  const z = zScore(p);
                  let zColor = "#6c757d";
                  if (z != null && z >= 0.7) zColor = "#c0504d";
                  else if (z != null && z <= -0.7) zColor = "rgb(17,135,171)";
                  filas.push(
                    <tr key={`p_${p.id_pregunta}_${idx}`}>
                      <td className="text-center">{p.id_instrumento}</td>
                      <td className="text-center">{p.id_pregunta}</td>
                      <td style={{ fontSize: "0.78rem" }}>{p.texto_pregunta}</td>
                      <td className="text-center">{p.respuesta}</td>
                      <td
                        style={{
                          fontSize: "0.78rem",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {(() => {
                          const ranges = _parseRanges(p.texto_respuesta);
                          if (!ranges) return p.texto_respuesta;
                          return (
                            <div className="d-flex flex-wrap gap-1">
                              {ranges.map((r) => (
                                <span
                                  key={r.n}
                                  className="badge border"
                                  style={{
                                    backgroundColor: "#f3f0ed",
                                    color: "#333",
                                    fontWeight: 500,
                                    fontSize: "0.68rem",
                                  }}
                                  title={`Subpregunta ${r.n}`}
                                >
                                  {r.n}
                                  <span style={{ color: "#aaa" }}>:</span>
                                  <strong>{r.val}</strong>
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-center fw-bold">{p.puntaje}</td>
                      <td className="text-center">{p.promedio_puntaje}</td>
                      <td className="text-center">{p.desviacion_puntaje}</td>
                      <td className="text-center">
                        {z == null ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span
                            className="badge"
                            style={{ backgroundColor: zColor, fontSize: "0.7rem" }}
                          >
                            {z > 0 ? "+" : ""}
                            {z.toFixed(1)}σ
                          </span>
                        )}
                      </td>
                    </tr>,
                  );
                });
                return filas;
              })()}
            </tbody>
          </Table>
        </div>
      )}
        </>
      )}

      <div className="my-3">{Volver}</div>
    </Container>
  );
};

export default FichaEvaluado;
